import React, { createContext, useState, useEffect, useCallback } from 'react';
import cognitoConfig, { getLogoutUrl } from './cognitoConfig';

export const AuthContext = createContext(null);

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('mol_access_token'));
  const [idToken, setIdToken] = useState(() => localStorage.getItem('mol_id_token'));
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [allCustomers, setAllCustomers] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    localStorage.removeItem('mol_access_token');
    localStorage.removeItem('mol_id_token');
    localStorage.removeItem('mol_refresh_token');
    setToken(null);
    setIdToken(null);
    setUser(null);
    setCustomer(null);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    window.location.href = getLogoutUrl();
  }, [clearSession]);

  const saveTokens = useCallback((tokens) => {
    if (tokens.access_token) {
      localStorage.setItem('mol_access_token', tokens.access_token);
      setToken(tokens.access_token);
      setLoading(true); // Keep loading until /api/me completes
    }
    if (tokens.id_token) {
      localStorage.setItem('mol_id_token', tokens.id_token);
      setIdToken(tokens.id_token);
    }
    if (tokens.refresh_token) {
      localStorage.setItem('mol_refresh_token', tokens.refresh_token);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const refreshToken = localStorage.getItem('mol_refresh_token');
    if (!refreshToken) {
      return null;
    }

    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const res = await fetch(`${apiBase}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        return null;
      }

      saveTokens(data.data || {});
      return data.data?.id_token || null;
    } catch (err) {
      console.error('Failed to refresh session:', err);
      return null;
    }
  }, [saveTokens]);

  // Fetch user profile from backend when we have an ID token
  // (ID token carries custom:customer_slug and custom:role claims)
  useEffect(() => {
    if (!idToken) {
      setLoading(false);
      return;
    }

    const fetchMe = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
        const fetchProfile = (tokenValue) => fetch(`${apiBase}/api/me`, {
          headers: { Authorization: `Bearer ${tokenValue}` },
        });

        let res = await fetchProfile(idToken);

        if (res.status === 401) {
          const refreshedIdToken = await refreshSession();
          if (!refreshedIdToken) {
            clearSession();
            setLoading(false);
            return;
          }

          res = await fetchProfile(refreshedIdToken);
          if (res.status === 401) {
            clearSession();
            setLoading(false);
            return;
          }
        }

        const data = await res.json();
        if (data.success) {
          setUser(data.data.user);
          setCustomer(data.data.customer);
          if (data.data.allCustomers) {
            setAllCustomers(data.data.allCustomers);
          }
        } else {
          // Don't clear session on non-401 errors to prevent redirect loop.
          // The user has valid Cognito tokens but the backend had an issue.
          console.error('Failed to load user profile:', data.error);
        }
      } catch (err) {
        // Network error — don't clear session to prevent redirect loop.
        console.error('Failed to fetch user profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMe();
  }, [idToken, clearSession, refreshSession]);

  const isAuthenticated = !!idToken && !!user;

  const value = {
    token,
    idToken,
    user,
    customer,
    allCustomers,
    loading,
    isAuthenticated,
    saveTokens,
    logout,
    clearSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
